import React, { useState, useEffect } from 'react';
import FormularioCredenciales, { OrigenCredenciales } from "../../../components/EstatusFacturas/FormularioCredencialesAriba.tsx";
import Alert from "../../../components/ui/Alert.tsx";
import Breadcrumbs from "../../../components/ui/Breadcrumbs.tsx";
import { Send, FileCode, Database, ShieldAlert } from "lucide-react";
import { getAuth } from "firebase/auth";
import { useProject } from "../../../context/ProjectContext.tsx";
import {
  obtenerConfigCliente,
  guardarConfigCliente,
  obtenerEstadoCodigoProduccion,
  verificarCodigoProduccion,
  CigConexionConfig,
  SystemIds
} from "../../../services/clienteConexionService.ts";

const SYSTEM_IDS_VACIOS: SystemIds = { dev: "", qas: "", prd: "" };

const EnvioDocumentos = ({ setPage }: { setPage: (page: string) => void }) => {
  const { project } = useProject();
  const clienteId = project?.clienteId || "";

  // --- ESTADOS (Mismos que StatusFactura para el Formulario) ---
  const [origenCredenciales, setOrigenCredenciales] = useState<OrigenCredenciales>("manual");
  const [tieneCredencialesGuardadas, setTieneCredencialesGuardadas] = useState(false);
  const [usuarioGuardado, setUsuarioGuardado] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [guardarCredenciales, setGuardarCredenciales] = useState(false);
  const [systemid, setSystemId] = useState("");
  const [ambiente, setAmbiente] = useState("");
  const [dataCenter, setDataCenter] = useState("");
  const [erpType, setErpType] = useState<"onprem" | "cloud" | "">("");
  const [systemIds, setSystemIds] = useState<SystemIds>(SYSTEM_IDS_VACIOS);

  // --- ESTADOS DE CONTROL DE PRODUCCIÓN ---
  const [showModal, setShowModal] = useState(false);
  const [prodCode, setProdCode] = useState("");
  const [isProdConfirmed, setIsProdConfirmed] = useState(false);
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  const [tieneCodigoConfigurado, setTieneCodigoConfigurado] = useState<boolean | null>(null);
  const [enviando, setEnviando] = useState(false);

  // --- ESTADOS ESPECÍFICOS DE ESTA VISTA ---
  const [documentType, setDocumentType] = useState(""); // Ej: Purchase Order, Receipt, etc.
  const [payload, setPayload] = useState(""); // Aquí va el IDOC o XML manual
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");

  const inputClass = "w-full bg-[rgb(var(--c-surface))] border border-white/10 rounded-xl p-3 text-white focus:border-blue-500 outline-none transition-all";
  const labelClass = "block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2 mt-4 ml-1";

  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 5000);
  };

  // Precarga Data Center / Tipo de ERP / System IDs guardados para este
  // cliente (ver Configuración), y si hay credenciales guardadas, las
  // ofrece como origen por defecto.
  useEffect(() => {
    if (!clienteId) return;
    obtenerConfigCliente<CigConexionConfig>(clienteId, "cig")
      .then((config) => {
        setDataCenter((prev) => prev || config.dataCenter);
        setErpType((prev) => prev || config.erpType);
        setSystemIds(config.systemIds);
        setTieneCredencialesGuardadas(config.tieneCredencialesGuardadas);
        setUsuarioGuardado(config.usuario);
        setOrigenCredenciales(config.tieneCredencialesGuardadas ? "guardadas" : "manual");
      })
      .catch((err) => console.error("Error cargando configuración de conexión:", err));
    obtenerEstadoCodigoProduccion(clienteId)
      .then((estado) => setTieneCodigoConfigurado(estado.tieneCodigoGuardado))
      .catch((err) => console.error("Error consultando código de producción:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const handleAmbienteChange = (value: string) => {
    setAmbiente(value);
    if (value === "prd") {
      setIsProdConfirmed(false);
      setShowModal(true);
    } else {
      setIsProdConfirmed(false);
    }
  };

  const handleConfirmProd = async () => {
    setVerificandoCodigo(true);
    try {
      const valido = await verificarCodigoProduccion(clienteId, prodCode);
      if (valido) {
        setIsProdConfirmed(true);
        setShowModal(false);
        showAlert("✅ Acceso a producción confirmado.", "success");
      } else {
        showAlert("❌ Código incorrecto. Inténtalo de nuevo.", "error");
      }
    } catch (err: any) {
      showAlert(err.message || "No se pudo validar el código.", "error");
    } finally {
      setVerificandoCodigo(false);
    }
  };

  const handleCancelModal = () => {
    setShowModal(false);
    setAmbiente("");
    setIsProdConfirmed(false);
    setProdCode("");
  };

  const handleSendDocument = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      showAlert("No se detectó una sesión activa.", "error");
      return;
    } 

    if (ambiente === "prd" && !isProdConfirmed) {
      showAlert("⚠️ Debes confirmar el código de producción antes de continuar.", "warning");
      return;
    }

    if (!payload.trim() || !documentType) {
      showAlert("Por favor selecciona un tipo de documento y pega el Payload.", "error");
      return;
    }

    setEnviando(true);
    try {
      if (origenCredenciales === "manual" && guardarCredenciales) {
        await guardarConfigCliente(clienteId, "cig", {
          dataCenter,
          erpType,
          systemIds,
          usuario: username,
          contraseña: password
        });
      }

      const token = await user.getIdToken();
      const tipo = origenCredenciales === "guardadas" ? 1 : 2;
      const credenciales = origenCredenciales === "guardadas" ? clienteId : `${username}:${password}`;

      const baseUrl = dataCenter === "eu" ? "managedgateway.cloud.sap" : "managedgateway.cloud.sap"; // Ajustar si cambia el subdominio
      const ambientePrefix = ambiente === "test" ? "test-integration" : "integration";
      const ambienteUrl = `https://${ambientePrefix}.${dataCenter}.${baseUrl}:443/cxf/receiveSOAP`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/webserviceCIG`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          tipo,
          credencials: credenciales,
          app: "cig",
          ambiente: ambienteUrl,
          body: payload,
        }),
      });

      const responseText = await response.text();
      if (response.ok) {
        showAlert("Documento inyectado correctamente a CIG.", "success");
      } else {
        showAlert(`Error: ${responseText}`, "error");
      }
    } catch (error: any) {
      showAlert(error.message || "Hubo un error al conectar con el servidor.", "error");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <main className="min-h-screen p-4 md:p-8 text-white bg-transparent">
      <header className="max-w-7xl mx-auto mb-10">
        <Breadcrumbs currentPage="envio-documentos" setPage={setPage} />
        <h1 className="text-4xl font-extrabold mt-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
          Document <span className="text-white/20 font-light">Bridge</span>
        </h1>
        <p className="text-gray-500 mt-2 font-light italic">Reenvío manual de transacciones ERP hacia SAP Ariba CIG</p>
      </header>

      <div className="max-w-7xl mx-auto">
        <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

          {/* COLUMNA IZQUIERDA: REUTILIZAMOS CONFIGURACIÓN */}
          <section className="lg:col-span-5 bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
            <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4">
              <div className="p-3 bg-blue-500/20 rounded-2xl text-blue-400"><Database size={24} /></div>
              <h2 className="text-xl font-bold">Configuración de Destino</h2>
            </div>

            <FormularioCredenciales
              origenCredenciales={origenCredenciales} setOrigenCredenciales={setOrigenCredenciales}
              tieneCredencialesGuardadas={tieneCredencialesGuardadas} usuarioGuardado={usuarioGuardado}
              username={username} password={password} setUsername={setUsername} setPassword={setPassword}
              guardarCredenciales={guardarCredenciales} setGuardarCredenciales={setGuardarCredenciales}
              ambiente={ambiente} setAmbiente={handleAmbienteChange}
              dataCenter={dataCenter} setDataCenter={setDataCenter} erpType={erpType} setErpType={setErpType}
              systemIds={systemIds} setSystemId={setSystemId}
            />
          </section>

          {/* COLUMNA DERECHA: EL PAYLOAD VARIABLE */}
          <section className="lg:col-span-7 space-y-6">
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-[2.5rem] shadow-2xl">
              <div className="flex items-center gap-3 mb-8 border-b border-white/5 pb-4">
                <div className="p-3 bg-indigo-500/20 rounded-2xl text-indigo-400"><FileCode size={24} /></div>
                <h2 className="text-xl font-bold">Cuerpo del Documento</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Tipo de Transacción (ERP Object)</label>
                  <select
                    className={inputClass}
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                  >
                    <option value="" className="bg-[rgb(var(--c-surface))]">Selecciona el tipo de documento</option>
                    <option value="PO" className="bg-[rgb(var(--c-surface))]">Purchase Order (Export)</option>
                    <option value="GR" className="bg-[rgb(var(--c-surface))]">Goods Receipt (Export)</option>
                    <option value="INV" className="bg-[rgb(var(--c-surface))]">Invoice (Import/Export)</option>
                    <option value="IDOC" className="bg-[rgb(var(--c-surface))]">IDOC Genérico / Custom</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Payload (XML / IDOC Structure)</label>
                  <textarea
                    className={`${inputClass} h-96 font-mono text-xs bg-black/40 border-indigo-500/30 text-indigo-200 p-4 resize-none`}
                    value={payload}
                    onChange={(e) => setPayload(e.target.value)}
                    placeholder={`<?xml version="1.0" encoding="UTF-8"?>\n<CombinedMessage>\n  \n</CombinedMessage>`}
                  />
                  <p className="text-[10px] text-gray-500 mt-2 italic px-2">
                    * El sistema inyectará automáticamente las cabeceras de autenticación según la configuración de la izquierda.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSendDocument}
              disabled={enviando}
              className="w-full py-5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 rounded-2xl font-bold text-white shadow-xl shadow-indigo-900/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3"
            >
              <Send size={20} />
              {enviando ? "Inyectando..." : "Inyectar Documento a CIG"}
            </button>
          </section>

        </div>
      </div>

      {/* MODAL DE SEGURIDAD PARA PRODUCCIÓN */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn">
          <div className="bg-[rgb(var(--c-bg))] border border-white/10 p-10 rounded-[2.5rem] max-w-md w-full shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="p-4 bg-red-500/20 rounded-full text-red-500 mb-6">
                <ShieldAlert size={48} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Confirmar Producción</h3>
              <p className="text-gray-400 mb-8 text-sm">Estás a punto de enviar datos reales al entorno de producción de SAP Ariba.</p>

              {tieneCodigoConfigurado === false ? (
                <p className="w-full bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-2xl p-4 text-xs mb-8 text-left">
                  Este cliente todavía no tiene un código de confirmación configurado. Pide a un administrador que lo configure en Configuración → CIG antes de enviar a Producción.
                </p>
              ) : (
                <input
                  type="password"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-center text-3xl tracking-[0.5em] mb-8 outline-none focus:border-red-500 transition-colors"
                  value={prodCode}
                  onChange={(e) => setProdCode(e.target.value)}
                  placeholder="****"
                  disabled={verificandoCodigo}
                  onKeyDown={(e) => e.key === "Enter" && !verificandoCodigo && handleConfirmProd()}
                />
              )}

              <div className="flex gap-4 w-full">
                <button
                  className="flex-1 py-4 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-2xl font-bold transition-colors"
                  onClick={handleConfirmProd}
                  disabled={tieneCodigoConfigurado === false || verificandoCodigo}
                >
                  {verificandoCodigo ? "Verificando..." : "Confirmar"}
                </button>
                <button className="flex-1 py-4 bg-white/5 hover:bg-white/10 rounded-2xl font-bold transition-colors" onClick={handleCancelModal}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default EnvioDocumentos;
